import { useEffect, useState } from 'react';

import { DownloadingOutlined } from '@mui/icons-material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
    Box,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    Radio,
    RadioGroup,
    Typography,
    useTheme,
} from '@mui/material';
import axios from 'axios';

import { useMutation } from '@redwoodjs/web';

import { REGENERATE_PRESIGNED_URL } from 'src/graphql/mutations/generateAwsURL.mutation';
import { GET_PRESIGNED_URL } from 'src/graphql/mutations/getPresignedURL.mutation';
import { downloadDocument, onError } from 'src/utils/utils';

interface ContractInformationSummaryProps {
    formSummaryData?: {
        partnerContractSignedAt: Date;
        contract: string;
    };
    setLastStep: (step: string) => void;
}

export const ContractInformation: React.FC<ContractInformationSummaryProps> = ({
    formSummaryData,
    setLastStep,
}) => {
    const [isDownloadingDocument, setIsDownloadingDocument] = useState(false);
    const [regenerateUrl] = useMutation(REGENERATE_PRESIGNED_URL);

    const [getPresignedURL] = useMutation(GET_PRESIGNED_URL, {
        onError,
    });
    const theme = useTheme();
    const [fileSize, setFileSize] = useState('');
    const fileName = formSummaryData?.contract?.split('/')?.reverse()[0];

    useEffect(() => {
        setLastStep('contractInformation');
        const getFileSizeFromS3 = async (url: string) => {
            try {
                const response = await fetch(url, { method: 'HEAD' });
                const contentLength = response.headers.get('content-length');
                const sizeInBytes = parseInt(contentLength || '0', 10);
                const sizeInKB = sizeInBytes / 1024;
                return `${sizeInKB.toFixed(2)} KB`;
            } catch (error) {
                return '';
            }
        };

        if (formSummaryData && formSummaryData.contract) {
            getFileSizeFromS3(formSummaryData.contract).then((size) => {
                setFileSize(size);
            });
        }

        return () => {
            setLastStep('');
        };
    }, [formSummaryData, setLastStep]);

    const handleViewContract = async () => {
        setIsDownloadingDocument(true);
        let mimeType = 'application/pdf';
        const [, fileExtension] = fileName.split('.');
        if (fileExtension === 'png') {
            mimeType = 'image/png';
        } else if (fileExtension === 'jpeg' || fileExtension === 'jpg') {
            mimeType = 'image/jpeg';
        }

        const { data } = await getPresignedURL({
            variables: {
                input: {
                    fileName,
                    mimeType,
                    referer: window.location.pathname.substring(1),
                },
            },
        });

        const fileUrl = data?.getSecureUploadUrl?.viewableSignedUrl;
        const downloadOptions = {
            fileUrl,
            fileName,
            isDownloadingToBrowser: true,
        };
        await downloadDocument(downloadOptions, async (url: string) => {
            const { data } = await regenerateUrl({ variables: { url } });
            const presignedUrl = data?.regenerateSecureAWSUrl;
            return await axios.get(presignedUrl, { responseType: 'blob' });
        });
        setIsDownloadingDocument(false);
    };

    return (
        <Grid item xs={12} sx={{ padding: '24px 24px' }} container>
            <Grid item xs={12} sx={{ borderBottom: '1px solid #F18F34', marginBottom: '24px' }}>
                <Box display="flex" flexDirection="column" gap="8px">
                    <Typography sx={{ fontSize: '24px', fontWeight: 500 }}>
                        Contract Generation
                    </Typography>
                </Box>
            </Grid>

            <Grid item xs={12} gap="24px" display="flex" flexDirection="column">
                <Grid item xs={6} display="flex" flexDirection="row" gap="24px" alignItems="center">
                    <Typography fontSize="16px" color="#575757" marginRight="21px">
                        Has the Partner contract been signed
                    </Typography>
                    <FormControl component="fieldset">
                        <RadioGroup
                            aria-labelledby="service-billed-radio-buttons"
                            name="service-billed-radio-group"
                            row
                            defaultValue={!!formSummaryData?.partnerContractSignedAt}
                            onChange={() => {}}
                        >
                            <FormControlLabel
                                value
                                control={<Radio disabled />}
                                label="Yes"
                                onClick={() => {}}
                            />
                            <FormControlLabel
                                value={false}
                                control={<Radio disabled />}
                                label="No"
                                onClick={() => {}}
                            />
                        </RadioGroup>
                    </FormControl>
                </Grid>

                {formSummaryData?.partnerContractSignedAt ? (
                    <Grid item xs={6} display="flex" flexDirection="column" gap="24px">
                        <Typography
                            sx={{
                                fontSize: '14px',
                                fontWeight: 600,
                            }}
                            color={theme.palette.dark.main}
                        >
                            Signed Copy of Contract
                        </Typography>

                        <Grid
                            item
                            xs={12}
                            container
                            sx={{
                                border: '1px solid #F18F34',
                                borderRadius: '8px',
                            }}
                        >
                            <Grid
                                item
                                lg={6}
                                xs={12}
                                display="flex"
                                flexDirection="row"
                                gap="24px"
                                alignItems="center"
                                padding="14px"
                            >
                                <Grid
                                    borderRadius="28px"
                                    sx={{ backgroundColor: '#FEEDAA' }}
                                    padding="9px"
                                >
                                    <InsertDriveFileIcon sx={{ color: '#F18F34' }} />
                                </Grid>
                                <Grid>
                                    <Typography
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: 600,
                                        }}
                                        color={theme.palette.dark.main}
                                    >
                                        Signed Contract.pdf
                                    </Typography>

                                    <Typography
                                        sx={{ fontSize: '14px', fontWeight: 400 }}
                                        color="#8E8E8D"
                                    >
                                        {fileSize}
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Grid
                                item
                                lg={6}
                                xs={12}
                                display="flex"
                                flexDirection="row"
                                justifyContent="flex-end"
                            >
                                <IconButton
                                    data-testid={`view-contract-btn`}
                                    onClick={() => {
                                        if (!isDownloadingDocument) {
                                            handleViewContract();
                                        }
                                    }}
                                >
                                    {isDownloadingDocument ? (
                                        <DownloadingOutlined sx={{ color: '#F18F34' }} />
                                    ) : (
                                        <VisibilityOutlinedIcon sx={{ color: '#F18F34' }} />
                                    )}
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Grid>
                ) : null}
            </Grid>
        </Grid>
    );
};
